import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Shuffle, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import NaijaAvatar from "@/components/NaijaAvatar";
import {
  SKINS, HAIR_COLORS, OUTFIT_COLORS, HAIRS, OUTFITS, ACCESSORIES, BODIES,
  normalizeAppearance, seededAppearance,
} from "@/lib/appearance";

const TABS = ["Body", "Skin", "Hair", "Outfit", "Extras"];

export default function Wardrobe() {
  const { character, setCharacter } = useAuth();
  const nav = useNavigate();
  const [look, setLook] = useState(normalizeAppearance(character?.appearance));
  const [tab, setTab] = useState("Body");
  const [busy, setBusy] = useState(false);

  if (!character) return null;
  const set = (k, v) => setLook((p) => ({ ...p, [k]: v }));
  const randomize = () => setLook(seededAppearance(Math.random().toString(36)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/character/appearance", { appearance: look });
      setCharacter(data.character);
      toast.success("Fresh look saved! 🔥");
      nav("/me");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] bg-[#F8FAFC]" data-testid="wardrobe-page">
      <div className="flex items-center gap-2 px-4 pt-4">
        <button onClick={() => nav(-1)} data-testid="wardrobe-back" className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display text-xl">Dressing Room</h1>
        <button onClick={randomize} data-testid="wardrobe-random" className="ml-auto w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center">
          <Shuffle size={16} className="text-[#16A34A]" />
        </button>
      </div>

      <div className="relative flex items-end justify-center h-[220px] bg-gradient-to-b from-[#16A34A]/10 to-transparent">
        <div className="absolute bottom-3 w-40 h-5 rounded-[50%] bg-black/15 blur-[3px]" />
        <motion.div key={JSON.stringify(look)} initial={{ scale: 0.96, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 180, damping: 16 }} data-testid="wardrobe-preview">
          <NaijaAvatar appearance={look} view="full" size={150} />
        </motion.div>
      </div>

      <div className="flex gap-1.5 px-4 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t} data-testid={`wtab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === t ? "bg-[#16A34A] text-white" : "bg-white text-slate-500 border border-slate-200"
            }`}>{t}</button>
        ))}
      </div>

      <div className="px-5 py-4 pb-28">
        {tab === "Body" && <Sec label="Build"><Chips options={BODIES} value={look.body} onPick={(v) => set("body", v)} t="wbody" /></Sec>}
        {tab === "Skin" && (
          <Sec label="Skin tone"><Row>{Object.entries(SKINS).map(([k, v]) => <Sw key={k} c={v.fill} a={look.skin === k} onClick={() => set("skin", k)} t={`wskin-${k}`} />)}</Row></Sec>
        )}
        {tab === "Hair" && (
          <>
            <Sec label="Style"><Chips options={HAIRS} value={look.hair} onPick={(v) => set("hair", v)} t="whair" /></Sec>
            <Sec label="Hair colour"><Row>{Object.entries(HAIR_COLORS).map(([k, v]) => <Sw key={k} c={v} a={look.hairColor === k} onClick={() => set("hairColor", k)} t={`whaircolor-${k}`} />)}</Row></Sec>
          </>
        )}
        {tab === "Outfit" && (
          <>
            <Sec label="Outfit"><Chips options={OUTFITS} value={look.outfit} onPick={(v) => set("outfit", v)} t="woutfit" /></Sec>
            <Sec label="Colour"><Row>{Object.entries(OUTFIT_COLORS).map(([k, v]) => <Sw key={k} c={v} a={look.outfitColor === k} onClick={() => set("outfitColor", k)} t={`woutfitcolor-${k}`} />)}</Row></Sec>
          </>
        )}
        {tab === "Extras" && <Sec label="Accessory"><Chips options={ACCESSORIES} value={look.accessory} onPick={(v) => set("accessory", v)} t="waccessory" /></Sec>}
      </div>

      <div className="fixed bottom-0 inset-x-0 max-w-md mx-auto p-4 bg-white border-t border-slate-200">
        <button onClick={save} disabled={busy} data-testid="wardrobe-save"
          className="w-full bg-[#16A34A] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
          <Check size={18} /> {busy ? "Saving…" : "Save new look"}
        </button>
      </div>
    </div>
  );
}

const Sec = ({ label, children }) => (<div className="mb-5"><div className="label-xs text-slate-500 mb-2">{label}</div>{children}</div>);
const Row = ({ children }) => <div className="flex gap-3 flex-wrap">{children}</div>;
const Chips = ({ options, value, onPick, t }) => (
  <div className="flex gap-2 flex-wrap">
    {options.map((o) => (
      <button key={o.id} data-testid={`${t}-${o.id}`} onClick={() => onPick(o.id)}
        className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${value === o.id ? "bg-[#16A34A] text-white border-[#16A34A] scale-105" : "bg-white text-slate-600 border-slate-200"}`}>{o.label}</button>
    ))}
  </div>
);
const Sw = ({ c, a, onClick, t }) => (
  <button data-testid={t} onClick={onClick} aria-label="colour"
    className={`w-11 h-11 rounded-full border-2 transition-transform ${a ? "scale-110 ring-2 ring-offset-2 ring-[#16A34A]" : "border-white"}`}
    style={{ background: c, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }} />
);
